/**
 * @file deployment skew (古い client bundle が、現在の build に存在しない Server Action や
 * chunk を要求する状態) の検知と通知。
 *
 * Server Action ID と chunk 名は build ごとに変わるため、deploy 前に開かれていたタブや
 * キャッシュされた HTML からの要求は必ず失敗する。self-host では Vercel の
 * Skew Protection が使えず、旧 build の資産を残すこともできないため、
 * この失敗は再読み込みでしか解消しない。アプリ側の不具合ではないので Bugsnag の
 * error 通知には載せず、再読み込みを促す UI に振り替える。
 */

/** Next.js が deployment skew 時に返すエラーメッセージ (小文字比較用)。 */
const STALE_SERVER_ACTION_PATTERNS = [
  // 現在の build に該当 ID の Server Action が存在しない
  "failed to find server action",
  "was not found on the server",
  // 別バージョンの Next.js が生成した ID 形式
  "server reference id did not match the expected format",
];

/**
 * client bundle の chunk 読み込み失敗を表すエラーメッセージ (小文字比較用)。
 * 旧 build の HTML / cache が現在の build に無い chunk を要求したときに出るため、
 * Server Action skew と同じく再読み込みでしか解消しない。
 * bundler ごとに文言が違うので webpack と Turbopack の両方を含める。
 */
const CHUNK_LOAD_PATTERNS = [
  // webpack: "Loading chunk 15820 failed." / "Loading CSS chunk 42 failed."
  "loading chunk",
  "loading css chunk",
  // Turbopack: "Failed to load chunk /_next/static/chunks/xxx.js"
  "failed to load chunk",
  // dynamic import の ESM 側エラー (Safari / Firefox)
  "importing a module script failed",
  "error loading dynamically imported module",
];

/** webpack が chunk 読み込み失敗時に投げる Error の `name`。 */
const CHUNK_LOAD_ERROR_NAME = "ChunkLoadError";

/** cause chain を辿る深さ。循環 cause による無限ループを避けるための上限。 */
const MAX_CAUSE_DEPTH = 3;

type StaleDeploymentListener = () => void;

const listeners = new Set<StaleDeploymentListener>();

function matchesStalePattern(message: string): boolean {
  const normalized = message.toLowerCase();
  return [...STALE_SERVER_ACTION_PATTERNS, ...CHUNK_LOAD_PATTERNS].some(
    (pattern) => normalized.includes(pattern),
  );
}

/**
 * errorClass / errorMessage の組から chunk 読み込み失敗かどうかを判定する。
 * Bugsnag の `onError` では originalError が失われている場合があるため、
 * event 側の平坦な文字列だけでも判定できるようにしている。
 * @param params 判定に使う error の class 名と message。
 * @returns chunk 読み込み失敗なら true。
 */
export function isChunkLoadErrorLike(params: {
  errorClass?: string;
  errorMessage?: string;
}): boolean {
  if (params.errorClass === CHUNK_LOAD_ERROR_NAME) return true;
  return params.errorMessage
    ? CHUNK_LOAD_PATTERNS.some((pattern) =>
        params.errorMessage?.toLowerCase().includes(pattern),
      )
    : false;
}

/**
 * 値が deployment skew 由来のエラーかどうかを判定する。
 * Server Action の ID 不一致と client chunk の読み込み失敗の両方を対象とする。
 * Error の `cause` に包まれて渡される場合があるため、cause chain も辿る。
 * @param value 判定対象。Error でも文字列でもよい。
 * @returns deployment skew 由来なら true。
 */
export function isStaleDeploymentError(value: unknown): boolean {
  if (typeof value === "string") {
    return matchesStalePattern(value);
  }

  let current: unknown = value;
  for (let depth = 0; depth <= MAX_CAUSE_DEPTH; depth++) {
    if (!(current instanceof Error)) return false;
    if (current.name === CHUNK_LOAD_ERROR_NAME) return true;
    if (matchesStalePattern(current.message)) return true;
    current = current.cause;
  }
  return false;
}

/**
 * deployment skew の検知を購読する。UI 側で再読み込み導線を出すために使う。
 * @param listener 検知時に呼ばれる callback。
 * @returns 購読を解除する関数。
 */
export function subscribeToStaleDeployment(
  listener: StaleDeploymentListener,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * deployment skew を検知したことを購読者に通知する。
 * 購読者が居ない (server 側や UI mount 前) 場合は何もしない。
 */
export function reportStaleDeployment(): void {
  for (const listener of listeners) {
    listener();
  }
}
